// src/lib/tabCloseSuppression.js
//
// CONFIRMED ROOT CAUSE (2026-10-12): TabPresenceWatcher.jsx's `pagehide`
// listener fires on ANY navigation away from the page, not just a real
// tab/browser close — there is no standard browser signal that
// distinguishes the two. This app does several hard
// `window.location.href` redirects while a user is still validly logged
// in (most frequently Dashboard.jsx's session-sync round trip through
// Authentik, which fires on every login/app-switch per
// NEW-APP-INTEGRATION.md's own notes on how often that happens) — each
// one blindly sent a real close-tab beacon, scheduling a close-triggered
// logout 8s later. If the redirect's round trip (often cross-origin,
// through Authentik and back) took longer than that grace window, a
// person who never closed anything got logged out. Confirmed live:
// several different accounts logged out with no idle timeout, no manual
// logout, and no real tab close, roughly 15-20s after a fresh login —
// consistent with this exact timing.
//
// Fix: every intentional hard navigation that expects a still-valid
// session to resume afterward (same tab, same or a different origin)
// must go through navigateAway()/reloadPage() below instead of calling
// window.location directly, so TabPresenceWatcher.jsx's pagehide handler
// can tell "we're intentionally leaving, don't treat this as a close"
// apart from a genuine tab close. sessionStorage, not a module variable
// — it has to survive the actual page unload this triggers.
//
// CONFIRMED BUG (2026-10-13), live: the original version stored a plain
// '1' with no expiry. A navigateAway() call fired BEFORE
// TabPresenceWatcher had ever mounted (e.g. SsoRedirect.js's pre-login
// redirect — TabPresenceWatcher only attaches its pagehide listener once
// `user` is truthy) sets the flag, but nothing is listening yet to
// consume it. That stale flag then sits in sessionStorage indefinitely
// and gets wrongly consumed by the NEXT pagehide — a genuine tab close,
// possibly minutes later, after a real login — silently swallowing that
// real close signal. Reproduced live: visiting Transfer's URL fresh (not
// yet authenticated) redirects straight to the SSO portal before any
// listener exists, logs in, lands back authenticated — closing that tab
// afterward never logs out, because the leftover flag from the pre-login
// redirect eats the real close. Fixed by storing a timestamp instead of
// a bare flag, and only honoring it within a short window — long enough
// to cover the gap between navigateAway() and the pagehide it causes,
// short enough that a navigation from minutes/hours earlier (whether or
// not anything ever consumed it) can never mask a later real close.

const SUPPRESS_KEY = 'bescom_suppress_tab_close';
// Generous for the actual gap (same-tick), nowhere near long enough to
// span an intervening login flow.
const SUPPRESS_MAX_AGE_MS = 5000;

function markNextPageHideAsExpected() {
  try {
    sessionStorage.setItem(SUPPRESS_KEY, String(Date.now()));
  } catch (_) {
    // Private-mode/storage-blocked browsers: worst case, the close
    // beacon fires anyway and we're back to relying on the grace window
    // — no worse than before this fix existed.
  }
}

// Navigate this tab to `url` (any origin) without TabPresenceWatcher.jsx
// treating it as a real tab close.
export function navigateAway(url) {
  markNextPageHideAsExpected();
  window.location.href = url;
}

// Same, for a same-page reload.
export function reloadPage() {
  markNextPageHideAsExpected();
  window.location.reload();
}

// Called once, by TabPresenceWatcher.jsx's own pagehide handler, to
// check AND consume the flag — always cleared on read (one-shot), so a
// later GENUINE close is never masked by a stale or already-used flag.
// Only honored if set recently enough to plausibly be THIS navigation.
export function consumeTabCloseSuppression() {
  try {
    const raw = sessionStorage.getItem(SUPPRESS_KEY);
    if (raw === null) return false;
    sessionStorage.removeItem(SUPPRESS_KEY);
    return Date.now() - Number(raw) < SUPPRESS_MAX_AGE_MS;
  } catch (_) {
    // If storage is blocked, there was never a flag to read either.
  }
  return false;
}
